import { Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { paths } from '../../routes/paths';
import { MetadataList, type MetadataItem } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import type { TransactionDetail } from './types';

export function CommissionCalculationPanel({
  detail,
  language,
  permissions,
}: {
  readonly detail: TransactionDetail;
  readonly language: TimeLanguage;
  readonly permissions: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const calculation = detail.calculation;
  if (calculation === null)
    return (
      <Paper component="section" sx={{ p: 2 }} variant="outlined">
        <Typography component="h2" variant="h5">
          {t('transactions.detail.calculation')}
        </Typography>
        <Typography color="text.secondary">{t('transactions.detail.noCalculation')}</Typography>
      </Paper>
    );
  const calculated = formatTimestamp(calculation.calculatedAt, 'Asia/Bangkok', language);
  const ruleLabel = t('transactions.detail.ruleVersion', {
    version: calculation.calculationVersion,
  });
  const rule = permissions.has('COMMISSION_RULE_VIEW') ? (
    <MuiLink
      component={Link}
      to={`${paths.commissionRules}/${encodeURIComponent(calculation.commissionRuleId)}`}
    >
      {ruleLabel}
    </MuiLink>
  ) : (
    ruleLabel
  );
  const items: readonly MetadataItem[] = [
    { label: t('transactions.fields.rule'), value: rule },
    { label: t('transactions.fields.assignmentId'), value: calculation.assignmentId },
    {
      label: t('transactions.fields.agentFormula'),
      value: `${detail.actualPlayAmount} × ${calculation.agentRatePercent}% = ${calculation.agentCommissionUnrounded} → ${calculation.agentCommission} ${detail.currencyCode}`,
    },
    {
      label: t('transactions.fields.affiliateFormula'),
      value: `${detail.actualPlayAmount} × ${calculation.affiliateRatePercent}% = ${calculation.affiliateCommissionUnrounded} → ${calculation.affiliateCommission} ${detail.currencyCode}`,
    },
    {
      label: t('transactions.fields.totalCommission'),
      value: `${detail.currencyCode}\u00a0${calculation.totalCommission}`,
    },
    { label: t('transactions.fields.roundingMode'), value: calculation.roundingMode },
    {
      label: t('transactions.fields.calculatedAt'),
      value: `${calculated.display} (${calculated.originalOffset})`,
    },
    { label: t('transactions.fields.calculatedBy'), value: calculation.calculatedBy },
  ];
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" sx={{ mb: 2 }} variant="h5">
        {t('transactions.detail.calculation')}
      </Typography>
      <MetadataList items={items} />
      <Typography component="h3" sx={{ mb: 1, mt: 3 }} variant="h6">
        {t('transactions.detail.ledgerSources')}
      </Typography>
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {detail.commissionLedgerEntries.map((entry) => (
          <Stack component="li" direction={{ xs: 'column', sm: 'row' }} key={entry.id} spacing={1}>
            {permissions.has('COMMISSION_LEDGER_VIEW') ? (
              <MuiLink
                component={Link}
                to={`${paths.commissionLedger}/${encodeURIComponent(entry.id)}`}
              >
                {t('transactions.detail.ledgerEntry', { id: entry.id })}
              </MuiLink>
            ) : (
              <Typography>{t('transactions.detail.ledgerEntry', { id: entry.id })}</Typography>
            )}
            <Typography>
              {entry.entryType} · {entry.currencyCode}\u00a0{entry.signedAmount}
            </Typography>
            {entry.walletPosting === null ? null : permissions.has('WALLET_VIEW') ? (
              <MuiLink
                component={Link}
                to={`${paths.affiliateWallets}/${encodeURIComponent(entry.walletPosting.walletId)}`}
              >
                {t('transactions.detail.walletLink', { id: entry.walletPosting.walletId })}
              </MuiLink>
            ) : (
              <Typography>
                {t('transactions.detail.walletLink', { id: entry.walletPosting.walletId })}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
